import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Collapse, Grid, Input, Modal } from 'antd';

import { api, readTotalCount } from '../../api/client';
import { API_COLLECTIONS_URL, API_ITEMS_URL } from '../../utils';
import DialogLoadState from '../common/DialogLoadState';
import GroupHeader from '../common/GroupHeader';
import { Skeleton } from '../loading';
import RecordBadge, { RecordIconTile, RecordTileStack } from '../common/RecordBadge';
import { CollectionFavoriteButton } from '../collections/CollectionActions';
import { FAVORITE_KIND, resolveFavoriteKind } from '../cards/CardVisuals';
import { selectQuickAddTemplates } from '../items/QuickAddButtons';
import { selectMultiAddForms } from '../items/MultiAddButtons';
import { FUNNEL, GRID, MAGNIFYING_GLASS } from '../icons';
import { chartFormGlyph } from '../reports/chartFormGlyphs';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { useOptionalFavorites } from '../../contexts/FavoritesContext';
import useEnsureCards from '../../hooks/useEnsureCards';
import { getQuickAddTemplateLists } from '../../services/quickAddTemplates';
import { getMultiAddFormLists } from '../../services/multiAddForms';
import { getChartLists, CHART_SCOPE } from '../../services/charts';
import { getDashboardLists, DASHBOARD_SCOPE } from '../../services/dashboards';
import { getSavedFilterLists } from '../../services/savedFilters';
import { FILTER_SCOPE } from '../../hooks/useSavedFilters';
import { PIN_KIND, TILE_SOURCE } from '../../services/favorites';
import RecordTileModal from './RecordTileModal';
import RecordListModal from './RecordListModal';
import { GROUP_LIMIT, NewestGroup, PinToggle, TileToggle } from './NewestGroup';
import { accessContextFor, launchHomeFor, parentKicker } from '../../utils/collectionFamily';
import { getCollectionFamilyCardIds, getCollectionFavoriteCardIds, getCollectionMemberCardIds, orderByFavorites } from '../../utils/collections';
import { isPersonal } from '../../utils/collectionKinds';
import { normalizeId } from '../../utils/ids';
import { canAddItemsNow, canCreateItems, canPinCollection, canRead, canReadCard, canReadCharts, canWriteCard, isGlobalConsumer, isLinkOnlyReader } from '../../utils/permissions';
import { compareByName } from '../../utils/sort';
import { serialize } from '../../utils/kql';
import { withSearchTerm } from '../../utils/kqlBuilder';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import { useCollections } from '../../contexts/CollectionsContext';

/**
 * "Add to Home": everything the person could pin, without hunting for it.
 *
 * Their collections as an accordion — the personal collection, then the
 * favorites in their own order, then the rest by name; a few at first,
 * "Show all" or the search box for the rest. Opening one (one at a time)
 * loads, for THAT collection only, what it offers:
 *
 *   Cards        "add an item of this card here" — the member cards the
 *                person can add items of (the collection home's add-item
 *                pin rule; a sub-collection's card is pinned there)
 *   Quick adds   the ones they can launch (either tier), no duplicate
 *                profiles (a sub-collection inherits none from above, KPR-1084)
 *   Multi adds   the same rule for forms
 *   Dashboards   the dashboards they can open here — pinned for everyone,
 *                and their own — as Home launchers
 *                (charts A7)
 *   Charts       the same for charts: a chart on Home, small at first (ruled
 *                d18), counting THIS collection's items
 *   Newest       "the newest record of this card" (KPR-1126) for each card
 *                whose items the person reads here, each with a picker of
 *                the collection's saved filters ("Every <card>" first) — the
 *                toggle pins that card under the filter chosen, and the tile
 *                button pins a tile of its values (KPR-1127). Not for a
 *                link-only reader (the pin's own gate)
 *   Filters      the collection's saved filters the person can see — pinned
 *                for everyone, and their own (KPR-1124): a shortcut that
 *                opens the list with the filter as it reads then
 *   Records      the ones changed most lately, the search box for the rest;
 *                each with a pin toggle and a tile button (KPR-1127)
 *
 * each a few rows with "Show all" (records: "Show more", a page at a time),
 * and a square pin toggle per row that says whether it is on Home now and
 * puts it there or takes it off (FavoritesContext.togglePin — the same call
 * every other pin makes, so Home behind the modal follows at once). The
 * collection's own star, in its header, is the favorite star everywhere.
 *
 * The gates are the ones the existing pin controls apply, read off the
 * collection's full document (GET /api/collections/:id — its myAccess and
 * its family); the API re-checks every pin on PUT and on every Home read.
 * On a phone the modal is a full-height sheet.
 */

const COLLECTION_LIMIT = 8;
const RECORD_FIRST_PAGE = 5;
const RECORD_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 250;

const matches = (text, term) => !term || String(text || '').toLowerCase().includes(term);


/** Where a chart or dashboard comes from, in the Reports page's words. */
const tierWord = forEveryone => (forEveryone ? 'Pinned for everyone' : 'Yours');

/** A chart's or dashboard's face: its glyph, its name, where it comes from. */
function ChartFace({ Glyph, name, meta }) {
	return (
		<span className="add-to-home__launcher add-to-home__launcher--chart">
			<Glyph aria-hidden="true" className="add-to-home__glyph" />
			<span className="add-to-home__launcher-text">
				<span className="add-to-home__launcher-name">{name}</span>
				{meta ? <span className="add-to-home__launcher-meta">{meta}</span> : null}
			</span>
		</span>
	);
}

/** A launcher's face: its card's tile (a stack for a multi add), its name, what it writes. */
function LauncherFace({ card, cards, name, meta }) {
	return (
		<span className="add-to-home__launcher">
			{cards
				? <RecordTileStack records={cards} kind="card" size="xs" />
				: <RecordIconTile record={card} kind="card" size="xs" />}
			<span className="add-to-home__launcher-text">
				<span className="add-to-home__launcher-name">{name}</span>
				{meta ? <span className="add-to-home__launcher-meta">{meta}</span> : null}
			</span>
		</span>
	);
}

/**
 * One group inside a collection: a GroupHeader, the first few rows, and
 * "Show all N" / "Show fewer". Rows are `{ key, name, face, pinned, onToggle }`.
 */
function PinGroup({ label, noun, rows }) {
	const [expanded, setExpanded] = useState(false);
	if (!rows.length) return null;
	const shown = expanded ? rows : rows.slice(0, GROUP_LIMIT);
	return (
		<section className="add-to-home__group" aria-label={label}>
			<GroupHeader label={label} count={rows.length} />
			<ul className="add-to-home__rows">
				{shown.map(row => (
					<li key={row.key} className="add-to-home__row">
						<span className="add-to-home__face">{row.face}</span>
						<PinToggle pinned={row.pinned} name={row.name} onToggle={row.onToggle} />
					</li>
				))}
			</ul>
			{rows.length > GROUP_LIMIT ? (
				<Button type="link" size="small" className="add-to-home__more" onClick={() => setExpanded(open => !open)}>
					{expanded ? 'Show fewer' : `Show all ${rows.length} ${noun}`}
				</Button>
			) : null}
		</section>
	);
}


const recordTitleOf = item => (typeof item?.displayValue === 'string' && item.displayValue.trim()) || 'Untitled item';

/**
 * A collection's records, a page at a time: the ones changed most lately,
 * or — with a search term — the ones whose text matches (`text ~ "term"`,
 * the list's own search box).
 */
function useCollectionRecords(collectionId, term) {
	const [state, setState] = useState({ rows: [], total: 0, loading: true, error: false });
	const requestRef = useRef(0);

	const fetchPage = useCallback((skip, limit, append) => {
		const token = ++requestRef.current;
		setState(current => ({ ...current, loading: true, error: false }));
		const params = { collection_id: collectionId, limit, skip, sort_field: 'updatedAt', sort_direction: 'desc' };
		const ast = withSearchTerm(null, term, 'text');
		if (ast) params.q = serialize(ast);
		api.get(API_ITEMS_URL, { params })
			.then(({ data, headers }) => {
				if (token !== requestRef.current) return;
				const page = Array.isArray(data) ? data : [];
				setState(current => {
					const rows = append ? [...current.rows, ...page] : page;
					return { rows, total: readTotalCount(headers, rows.length), loading: false, error: false };
				});
			})
			.catch(() => {
				if (token !== requestRef.current) return;
				setState(current => ({ ...current, loading: false, error: true }));
			});
	}, [collectionId, term]);

	useEffect(() => {
		if (!collectionId) return undefined;
		fetchPage(0, RECORD_FIRST_PAGE, false);
		return () => { requestRef.current += 1; };
	}, [collectionId, fetchPage]);

	const loadMore = useCallback(() => fetchPage(state.rows.length, RECORD_PAGE, true), [fetchPage, state.rows.length]);
	// Retry asks for the page that failed: the first, or the next one.
	const retry = useCallback(() => {
		if (state.rows.length) fetchPage(state.rows.length, RECORD_PAGE, true);
		else fetchPage(0, RECORD_FIRST_PAGE, false);
	}, [fetchPage, state.rows.length]);
	return { ...state, loadMore, retry };
}

/**
 * One of a collection's lists the panel offers pins from (quick adds, multi
 * adds, charts, dashboards): `status` is 'idle' until the read is wanted,
 * 'loading' while it is out, then 'ok' or 'error'. A failed read is never an
 * empty list, so the panel can say it failed and offer Retry (section 14).
 */
function usePinList(enabled, collectionId, read) {
	const [list, setList] = useState([]);
	const [status, setStatus] = useState('idle');
	const [attempt, setAttempt] = useState(0);
	const readRef = useRef(read);
	readRef.current = read;
	useEffect(() => {
		if (!enabled) return undefined;
		let active = true;
		setStatus('loading');
		readRef.current(collectionId)
			.then(next => { if (active) { setList(next); setStatus('ok'); } })
			.catch(() => { if (active) { setList([]); setStatus('error'); } });
		return () => { active = false; };
	}, [enabled, collectionId, attempt]);
	const retry = useCallback(() => setAttempt(n => n + 1), []);
	return { list, status, retry };
}

/**
 * What one collection offers to pin. Mounted when its panel first opens
 * (antd's Collapse renders a panel's children lazily), so nothing is
 * fetched for a collection the person never opens.
 */
export function CollectionPins({ collectionId, name }) {
	const favorites = useOptionalFavorites();
	const { cards, cardsById } = useCardDefinitions();
	const [collection, setCollection] = useState(null);
	const [loadError, setLoadError] = useState(false);
	const [collectionAttempt, setCollectionAttempt] = useState(0);
	const [searchText, setSearchText] = useState('');
	const [term, setTerm] = useState('');
	// The tile whose values are being picked (KPR-1127): `{ target, cardId, name }`.
	const [tiling, setTiling] = useState(null);
	// The list whose count is being picked (KPR-1128): `{ target, name }`.
	const [listing, setListing] = useState(null);

	// `term` goes to the server as typed (the records' `text ~`); `needle`
	// narrows the groups this panel already holds.
	useEffect(() => {
		const handle = setTimeout(() => setTerm(searchText.trim()), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(handle);
	}, [searchText]);
	const needle = term.toLowerCase();

	// The FULL document: its myAccess, its cards and its family decide what
	// may be pinned — the list row does not carry the family.
	useEffect(() => {
		let active = true;
		setLoadError(false);
		api.get(`${API_COLLECTIONS_URL}/${collectionId}`)
			.then(({ data }) => { if (active) setCollection(data || null); })
			.catch(() => { if (active) setLoadError(true); });
		return () => { active = false; };
	}, [collectionId, collectionAttempt]);

	// A launcher pin is refused in an archived collection (its items are
	// read-only), so none is offered there — and the panel says why.
	const canAdd = Boolean(collection) && canAddItemsNow(collection);
	const archivedWriter = Boolean(collection) && collection.status === 'archived' && canCreateItems(collection);
	const quickList = usePinList(canAdd, collectionId, id => getQuickAddTemplateLists(id).then(({ templates }) => templates));
	const multiList = usePinList(canAdd, collectionId, id => getMultiAddFormLists(id).then(({ forms }) => forms));
	const templates = quickList.list;
	const forms = multiList.list;

	// Charts and dashboards (A7): for people who read the collection
	// themselves (d14) — a global consumer or link-only reader is offered none.
	const chartsAllowed = Boolean(collection) && canReadCharts(collection);
	const chartsRead = usePinList(chartsAllowed, collectionId, id => getChartLists(id).then(({ charts }) => charts));
	const dashboardsRead = usePinList(chartsAllowed, collectionId, id => getDashboardLists(id).then(({ dashboards }) => dashboards));
	const chartList = chartsRead.list;
	const dashboardList = dashboardsRead.list;

	// The collection's saved filters, read once for two groups: Filters
	// (KPR-1124) — the same list the collection's List reads, the collection
	// tier and the person's own — and the Newest group's picker (KPR-1126),
	// for anyone who reads items here. Never for a link-only reader: neither
	// pin's gate admits one.
	const filtersAllowed = Boolean(collection) && !isLinkOnlyReader(collection);
	const newestAllowed = filtersAllowed && canRead(collection);
	const filtersRead = usePinList(filtersAllowed, collectionId, id => getSavedFilterLists(id).then(({ filters }) => filters));
	const filterList = filtersRead.list;

	const records = useCollectionRecords(collection ? collectionId : null, term);
	useEnsureCards(records.rows);

	const isPinned = (kind, targetId) => Boolean(favorites?.isPinned?.(kind, targetId, collectionId));
	const toggle = (kind, targetId, targetCollectionId = collectionId) => () => {
		favorites?.togglePin?.(kind, { targetId, collectionId: targetCollectionId }, { quiet: true });
	};

	// ---- Cards: member cards the person can add items of here ----
	const cardRows = useMemo(() => {
		if (!canAdd) return [];
		const memberIds = getCollectionMemberCardIds(collection, cards || []);
		const collectionFavorites = getCollectionFavoriteCardIds(collection);
		const isUserFavorite = id => Boolean(favorites?.isFavoriteCard?.(collectionId, id));
		const rank = id => {
			const scope = resolveFavoriteKind(id, collectionFavorites, isUserFavorite);
			return scope === FAVORITE_KIND.COLLECTION ? 0 : scope === FAVORITE_KIND.USER ? 1 : 2;
		};
		return (cards || [])
			.filter(card => {
				const id = normalizeId(card?._id);
				return id && memberIds.has(id)
					&& !launchHomeFor(collection, id)
					&& canWriteCard(accessContextFor(collection, id), id);
			})
			.sort((a, b) => (rank(normalizeId(a._id)) - rank(normalizeId(b._id))) || compareByName(a, b));
	}, [canAdd, collection, cards, favorites, collectionId]);

	// ---- Newest: every card whose items the person reads in this list ----
	const newestCards = useMemo(() => {
		if (!newestAllowed) return [];
		const familyIds = getCollectionFamilyCardIds(collection, cards || []);
		return (cards || [])
			.filter(card => {
				const id = normalizeId(card?._id);
				return id && familyIds.has(id) && canReadCard(accessContextFor(collection, id), id);
			})
			.sort(compareByName);
	}, [newestAllowed, collection, cards]);

	const quickAdds = useMemo(() => (canAdd
		? selectQuickAddTemplates({ collection, templates, cardsById }).filter(entry => !entry.home && !entry.from)
		: []), [canAdd, collection, templates, cardsById]);
	const multiAdds = useMemo(() => (canAdd
		? selectMultiAddForms({ collection, forms, cardsById }).filter(entry => !entry.from)
		: []), [canAdd, collection, forms, cardsById]);

	if (!collection) {
		return (
			<DialogLoadState
				loaded={false}
				error={loadError}
				label={name}
				onRetry={() => setCollectionAttempt(n => n + 1)}
				skeleton={<Skeleton variant="list-row" count={3} />}
				minHeight={120}
			/>
		);
	}
	// The lists this panel reads on top of the collection: which are still out, and which failed.
	const secondary = [
		{ read: quickList, label: 'the quick adds' },
		{ read: multiList, label: 'the multi adds' },
		{ read: dashboardsRead, label: 'the dashboards' },
		{ read: chartsRead, label: 'the charts' },
		{ read: filtersRead, label: 'the saved filters' },
	];
	const wanted = read => {
		if (read === quickList || read === multiList) return canAdd;
		if (read === filtersRead) return filtersAllowed;
		return chartsAllowed;
	};
	const secondaryWaiting = secondary.some(({ read }) => read.status === 'loading' || (read.status === 'idle' && wanted(read)));
	const secondaryFailed = secondary.filter(({ read }) => read.status === 'error');

	const cardGroup = cardRows
		.filter(card => matches(card.name, needle))
		.map(card => {
			const id = normalizeId(card._id);
			return {
				key: id,
				name: card.name || 'card',
				face: <RecordBadge kind="card" record={card} title={card.name || 'Untitled card'} size="sm" />,
				pinned: isPinned(PIN_KIND.ADD_ITEM, id),
				onToggle: toggle(PIN_KIND.ADD_ITEM, id),
			};
		});
	const quickAddGroup = quickAdds
		.filter(({ template, card }) => matches(template.name, needle) || matches(card?.name, needle))
		.map(({ template, card }) => {
			const id = normalizeId(template._id);
			return {
				key: id,
				name: template.name || 'quick add',
				face: <LauncherFace card={card} name={template.name || '(untitled quick add)'} meta={card?.name} />,
				pinned: isPinned(PIN_KIND.QUICK_ADD, id),
				onToggle: toggle(PIN_KIND.QUICK_ADD, id),
			};
		});
	const multiAddGroup = multiAdds
		.filter(({ form, cards: stepCards }) => matches(form.name, needle) || stepCards.some(card => matches(card?.name, needle)))
		.map(({ form, cards: stepCards }) => {
			const id = normalizeId(form._id);
			return {
				key: id,
				name: form.name || 'multi add',
				face: (
					<LauncherFace
						cards={stepCards}
						name={form.name || '(untitled multi add)'}
						meta={stepCards.map(card => card?.name).filter(Boolean).join(', ')}
					/>
				),
				pinned: isPinned(PIN_KIND.MULTI_ADD, id),
				onToggle: toggle(PIN_KIND.MULTI_ADD, id),
			};
		});
	const dashboardGroup = dashboardList
		.filter(doc => matches(doc?.name, needle))
		.map(doc => {
			const id = normalizeId(doc._id);
			return {
				key: id,
				name: doc.name || 'dashboard',
				face: <ChartFace Glyph={GRID} name={doc.name || 'Untitled dashboard'} meta={tierWord(doc.scope === DASHBOARD_SCOPE.COLLECTION)} />,
				pinned: isPinned(PIN_KIND.DASHBOARD, id),
				onToggle: toggle(PIN_KIND.DASHBOARD, id),
			};
		});
	const chartGroup = chartList
		.filter(doc => matches(doc?.name, needle))
		.map(doc => {
			const id = normalizeId(doc._id);
			return {
				key: id,
				name: doc.name || 'chart',
				face: <ChartFace Glyph={chartFormGlyph(doc.spec?.show?.type)} name={doc.name || 'Untitled chart'} meta={tierWord(doc.scope === CHART_SCOPE.COLLECTION)} />,
				pinned: isPinned(PIN_KIND.CHART_HOME, id),
				onToggle: toggle(PIN_KIND.CHART_HOME, id),
			};
		});
	const filterGroup = filterList
		.filter(doc => !doc?.pending && matches(doc?.name, needle))
		.map(doc => {
			const id = normalizeId(doc._id);
			return {
				key: id,
				name: doc.name || 'filter',
				face: <ChartFace Glyph={FUNNEL} name={doc.name || 'Untitled filter'} meta={tierWord(doc.scope === FILTER_SCOPE.COLLECTION)} />,
				pinned: isPinned(PIN_KIND.SAVED_FILTER, id),
				onToggle: toggle(PIN_KIND.SAVED_FILTER, id),
			};
		});
	// Record tiles (KPR-1127): a record of the list, and the newest record of
	// a card (below).
	const canTile = Boolean(favorites?.saveTile && favorites?.tileFor);
	const recordTileTarget = item => ({ source: TILE_SOURCE.RECORD, targetId: normalizeId(item._id), collectionId: normalizeId(item.collection_id) || collectionId });
	const recordRows = records.rows.map(item => {
		const id = normalizeId(item._id);
		const card = cardsById?.[normalizeId(item.card_id)] || null;
		const title = recordTitleOf(item);
		return {
			key: id,
			name: title,
			tiled: canTile && Array.isArray(favorites.tileFor(recordTileTarget(item))),
			onTile: canTile ? () => setTiling({ target: recordTileTarget(item), cardId: normalizeId(item.card_id), name: title }) : null,
			face: (
				<RecordBadge
					kind="item"
					record={card}
					kicker={card?.label?.singular || card?.name || undefined}
					title={title}
					size="sm"
				/>
			),
			pinned: isPinned(PIN_KIND.ITEM, id),
			// A record is pinned by its own id, where it lives now.
			onToggle: toggle(PIN_KIND.ITEM, id, normalizeId(item.collection_id) || collectionId),
		};
	});

	const newestGroup = newestCards.filter(card => matches(card.name, needle) || matches(`newest ${card.name}`, needle));
	const isNewestPinned = (cardId, filterId) => Boolean(favorites?.isPinned?.(PIN_KIND.LATEST_RECORD, cardId, collectionId, filterId));
	const toggleNewest = (cardId, filterId) => {
		favorites?.togglePin?.(PIN_KIND.LATEST_RECORD, { targetId: cardId, collectionId, savedFilterId: filterId }, { quiet: true });
	};
	// Record tiles (KPR-1127): the newest record of a card under the filter
	// chosen (the tile button on each Newest row).
	const newestTileTarget = (cardId, filterId) => ({ source: TILE_SOURCE.LATEST, targetId: cardId, collectionId, savedFilterId: filterId || null });
	const isNewestTiled = (cardId, filterId) => Array.isArray(favorites?.tileFor?.(newestTileTarget(cardId, filterId)));
	const tileNewest = ({ card, filterId, name: tileName }) => setTiling({
		target: newestTileTarget(normalizeId(card._id), filterId),
		cardId: normalizeId(card._id),
		name: tileName,
	});
	// Lists of records (KPR-1128): the card under the filter chosen (the list button on each Newest row).
	const canList = Boolean(favorites?.saveList && favorites?.listFor);
	const listTarget = (cardId, filterId) => ({ targetId: cardId, collectionId, savedFilterId: filterId || null });
	const isNewestListed = (cardId, filterId) => Number.isInteger(favorites?.listFor?.(listTarget(cardId, filterId)));
	const listNewest = ({ card, filterId, name: listName }) => setListing({ target: listTarget(normalizeId(card._id), filterId), name: listName });

	const nothingYet = !term && !cardGroup.length && !newestGroup.length && !quickAddGroup.length && !multiAddGroup.length
		&& !dashboardGroup.length && !chartGroup.length && !filterGroup.length
		&& !records.loading && !records.error && !recordRows.length
		&& !secondaryWaiting && !secondaryFailed.length;

	return (
		<div className="add-to-home__collection">
			<Input
				{...NO_TEXT_ASSIST}
				className="add-to-home__search"
				allowClear
				prefix={<MAGNIFYING_GLASS />}
				placeholder={`Search ${name}…`}
				aria-label={`Search ${name}`}
				value={searchText}
				onChange={event => setSearchText(event.target.value)}
			/>
			{archivedWriter ? (
				<p className="add-to-home__quiet" role="note">
					{name} is archived, so its items are read-only — there is nothing to launch here. Its records can still be pinned.
				</p>
			) : null}
			{nothingYet && !archivedWriter ? (
				<p className="add-to-home__quiet">
					{canAdd
						? `Nothing to pin here yet — give ${name} a card first.`
						: `Nothing here to pin yet — ${name} has no records you can open.`}
				</p>
			) : null}
			{secondaryFailed.map(({ read, label }) => (
				<DialogLoadState key={label} loaded={false} error label={label} onRetry={read.retry} minHeight={0} />
			))}
			<PinGroup label="Cards" noun="cards" rows={cardGroup} />
			<NewestGroup
				cards={newestGroup}
				filters={filtersRead.list}
				collectionId={collectionId}
				isPinned={isNewestPinned}
				onToggle={toggleNewest}
				isTiled={isNewestTiled}
				onTile={canTile ? tileNewest : null}
				isListed={isNewestListed}
				onList={canList ? listNewest : null}
			/>
			<PinGroup label="Quick adds" noun="quick adds" rows={quickAddGroup} />
			<PinGroup label="Multi adds" noun="multi adds" rows={multiAddGroup} />
			<PinGroup label="Dashboards" noun="dashboards" rows={dashboardGroup} />
			<PinGroup label="Charts" noun="charts" rows={chartGroup} />
			<PinGroup label="Filters" noun="filters" rows={filterGroup} />
			{recordRows.length ? (
				<section className="add-to-home__group" aria-label="Records">
					<GroupHeader label="Records" count={records.total} />
					<ul className="add-to-home__rows">
						{recordRows.map(row => (
							<li key={row.key} className="add-to-home__row">
								<span className="add-to-home__face">{row.face}</span>
								<PinToggle pinned={row.pinned} name={row.name} onToggle={row.onToggle} />
								{row.onTile ? <TileToggle tiled={row.tiled} name={row.name} onOpen={row.onTile} /> : null}
							</li>
						))}
					</ul>
					{recordRows.length < records.total ? (
						<Button type="link" size="small" className="add-to-home__more" loading={records.loading} onClick={records.loadMore}>
							Show more
						</Button>
					) : null}
				</section>
			) : null}
			{(records.loading && !recordRows.length) || secondaryWaiting ? (
				<DialogLoadState loaded={false} loading label="what you can pin" skeleton={<Skeleton variant="list-row" count={2} />} minHeight={0} />
			) : null}
			{records.error ? (
				<DialogLoadState loaded={false} error label="the records" onRetry={records.retry} minHeight={0} />
			) : null}
			{term && !records.loading && !records.error && !secondaryWaiting && !secondaryFailed.length && !recordRows.length && !cardGroup.length && !newestGroup.length && !quickAddGroup.length && !multiAddGroup.length && !dashboardGroup.length && !chartGroup.length && !filterGroup.length ? (
				<p className="add-to-home__quiet">Nothing in {name} matches “{searchText.trim()}”.</p>
			) : null}
			{tiling ? (
				<RecordTileModal
					open
					onClose={() => setTiling(null)}
					target={tiling.target}
					cardId={tiling.cardId}
					name={tiling.name}
				/>
			) : null}
			{listing ? <RecordListModal open onClose={() => setListing(null)} target={listing.target} name={listing.name} /> : null}
		</div>
	);
}

/** Keeps a click or a key on the header's star from also opening the panel. */
const stop = event => event.stopPropagation();

export default function AddToHomeModal({ open, onClose }) {
	const screens = Grid.useBreakpoint();
	const phone = screens.md === false;
	const favorites = useOptionalFavorites();
	// The shared collections list, asked again when the modal opens so a
	// collection made a moment ago is there.
	const { collections: sharedCollections, available: collectionsLoaded, error: collectionsError, refresh: refreshCollections } = useCollections({ refreshOnOpen: open });
	const collections = collectionsLoaded ? sharedCollections : null;
	const loadError = !collectionsLoaded && Boolean(collectionsError);
	const [searchText, setSearchText] = useState('');
	const [showAll, setShowAll] = useState(false);
	const [openKey, setOpenKey] = useState(null);

	const favoriteOrder = favorites?.favoriteCollectionOrder;
	const isFavorite = useCallback(id => Boolean(favorites?.isFavoriteCollection?.(id)), [favorites]);

	// The personal collection, then the favorites in the person's order,
	// then everything else by name.
	const ordered = useMemo(() => {
		const list = collections || [];
		const personal = list.filter(isPersonal);
		const starred = orderByFavorites(list.filter(c => !isPersonal(c) && isFavorite(c._id)), favoriteOrder);
		const rest = list
			.filter(c => !isPersonal(c) && !isFavorite(c._id))
			.sort(compareByName);
		return [...personal, ...starred, ...rest];
	}, [collections, favoriteOrder, isFavorite]);

	const term = searchText.trim().toLowerCase();
	const filtered = term
		? ordered.filter(c => matches(c.name, term) || matches(parentKicker(c), term))
		: ordered;
	const visible = term || showAll ? filtered : filtered.slice(0, COLLECTION_LIMIT);

	const items = visible.map(collection => {
		const id = normalizeId(collection._id);
		const name = collection.name || 'Untitled collection';
		// The personal collection is always on Home; a global collection's
		// consumer follows it instead — neither carries a star (the rule the
		// collection bar applies).
		const starrable = !isPersonal(collection) && !isGlobalConsumer(collection)
			&& typeof favorites?.toggleCollectionFavorite === 'function';
		const shortcutable = canPinCollection(collection) && typeof favorites?.togglePin === 'function';
		return {
			key: id,
			label: (
				<RecordBadge
					kind="collection"
					record={collection}
					kicker={parentKicker(collection) || undefined}
					title={name}
					size="sm"
				/>
			),
			// The collection itself, as a Home shortcut (KPR-1125, its own pin:
			// the star beside it is the favorite and is a different thing).
			// Sub-collections are in this list, named under their parent.
			extra: starrable || shortcutable ? (
				<span className="add-to-home__star" onClick={stop} onKeyDown={stop}>
					{shortcutable ? (
						<PinToggle
							pinned={Boolean(favorites.isPinned?.(PIN_KIND.COLLECTION_LINK, id))}
							name={name}
							onToggle={() => favorites.togglePin(PIN_KIND.COLLECTION_LINK, { targetId: id }, { quiet: true })}
						/>
					) : null}
					{starrable ? (
						<CollectionFavoriteButton
							className="collection-actions__button add-to-home__star-button"
							isFavorite={isFavorite(id)}
							onToggleFavorite={() => favorites.toggleCollectionFavorite(id, collection)}
						/>
					) : null}
				</span>
			) : null,
			children: <CollectionPins collectionId={id} name={name} />,
		};
	});

	return (
		<Modal
			open={open}
			title="Add to Home"
			onCancel={onClose}
			footer={<Button onClick={onClose}>Done</Button>}
			width={phone ? '100%' : 720}
			centered={!phone}
			destroyOnHidden
			className={`add-to-home${phone ? ' add-to-home--phone' : ''}`}
		>
			<p className="add-to-home__intro">
				Open a collection to pin its cards, the newest record of a card or a list of its records, quick adds, multi adds, dashboards, charts, filters and records. The pin beside a collection shortcuts the collection itself on Home; the star adds it to your favorites.
			</p>
			<Input
				{...NO_TEXT_ASSIST}
				className="add-to-home__search"
				allowClear
				prefix={<MAGNIFYING_GLASS />}
				placeholder="Search collections…"
				aria-label="Search collections"
				value={searchText}
				onChange={event => setSearchText(event.target.value)}
			/>
			<DialogLoadState
				loaded={collections !== null}
				error={loadError}
				label="your collections"
				onRetry={() => refreshCollections()}
				skeleton={<Skeleton variant="list-row" count={4} />}
				minHeight={160}
			>
				{!ordered.length ? (
					<p className="add-to-home__quiet">No collections yet — create one from Collections, then pin what you use most.</p>
				) : !filtered.length ? (
					<p className="add-to-home__quiet">No collections match “{searchText.trim()}”.</p>
				) : (
					<Collapse
						accordion
						className="add-to-home__collections"
						activeKey={openKey ? [openKey] : []}
						onChange={key => setOpenKey((Array.isArray(key) ? key[0] : key) || null)}
						items={items}
					/>
				)}
			</DialogLoadState>
			{!term && filtered.length > COLLECTION_LIMIT ? (
				<Button type="link" size="small" className="add-to-home__more" onClick={() => setShowAll(all => !all)}>
					{showAll ? 'Show fewer' : `Show all ${filtered.length} collections`}
				</Button>
			) : null}
		</Modal>
	);
}
